import {Injectable} from '@angular/core';
import {
    AuditId,
    ProjectId,
    SpecId,
    FindingId,
    TestRunId,
    ScreenshotId,
    ArtifactId, VideoRecordingId, UserId
} from '@webmate-web-core/shared/types/ids';
import {
    ContentCell,
    ObservationTarget,
    Finding,
    FindingCreationData,
    SingleTestRunCreationSpec,
    TestRun,
    FindingInfo,
    searchRecordToFindingInfo,
    FindingType,
    FindingSeverity,
    ContentCellId
} from '@webmate-web-core/shared/types/testmgmt';
import {combineLatest, forkJoin, from, Observable, of, throwError} from 'rxjs';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {concatMap, delay, filter, map, mergeMap, switchMap, take, tap, toArray} from 'rxjs/operators';
import {WorkbenchContextService} from './workbench-context.service';
import {FindingSearchRecord} from '@webmate-web-core/projects/project/testlab/project-findings.component';
import {ConsoleTestRunService} from '../../../webmate/shared/services/console-test-run.service';
import {IssueSeverity} from '../../../webmate/shared/console/types/shared';
import {getRandomUUID} from '@webmate-web-core/shared/utils/utils';
import {WorkbenchAuditService} from './workbench-audit.service';
import {DateTime} from '@webmate-web-core/shared/types/base-types';
import {Tag} from '@webmate-web-core/shared/types/tag';
import moment from 'moment';
import {FindingCellInsertService} from '@webmate-web-core/shared/services/finding-cell-insert.service';


/**
 * Test related behavior specific to the workbench.
 */
@Injectable()
export class WorkbenchTestService {

    // this is a Finding (or rather update data to create one) that does not exist in the backend,
    // and it will be created as soon as the user adds a cell to it.
    // It is to prevent empty findings that are only created because the user activated an audit activity.
    private currentPseudoActiveFinding?: FindingCreationData;

    constructor(private readonly workbenchContextService: WorkbenchContextService,
                private testService: TestService,
                private readonly workbenchAuditService: WorkbenchAuditService,
                private readonly findingCellInsertService: FindingCellInsertService) {}

    startAdHocTest(testName?: string): Observable<TestRun> {
        const projectId = this.workbenchContextService.getProjectId();
        const testRunCreationSpec = {
            type: 'SingleTestRunCreationSpec',
            assignmentSpec: {}
        } as SingleTestRunCreationSpec;

        return this.testService.createAndStartTestExecutionFromNamedTemplate(projectId, ConsoleTestRunService.ADHOC_TESTNAME, testName || ('Manual Test at ' + moment().format('DD.MM.YYYY HH:mm')), testRunCreationSpec, 'manualtest').pipe(
            map(result => result.testRunId),
            // Prevent race in backend
            delay(2500),
            mergeMap(testRunId => {
                // test run id is defined, as we started the execution
                return this.testService.fetchTestRunWithRetry(testRunId!, 1000);
            }),
            tap((testRun) => {
                this.workbenchContextService.setContextActiveTestRun(testRun.testRunId);
            }));
    }

    /**
     * Ensures there is an active manual (ad-hoc) test for the current user: resumes the user's existing
     * active ad-hoc run if one exists, otherwise starts a new one. Either way the active test run is set
     * in the workbench context and the workbench is switched into manual-test mode (without changing the
     * visible view). The emitted TestRun marks completion; callers that need to read the active test run
     * from the context should still wait for the context to reflect it (the update is applied
     * asynchronously via {@link WorkbenchContextService}).
     */
    startOrResumeAdHocTest(): Observable<TestRun> {
        const projectId = this.workbenchContextService.getProjectId();
        const userId = this.workbenchContextService.getUserId();

        return this.testService.getActiveAdhocTestRunsForUser$(projectId, userId).pipe(
            take(1),
            switchMap(runs => {
                if (runs.length > 0) {
                    this.workbenchContextService.setContextActiveTestRun(runs[0].testRunId);
                    return this.testService.getTestRun(runs[0].testRunId).pipe(take(1));
                }
                return this.startAdHocTest();
            }),
            tap(() => this.workbenchContextService.setActiveModeKind('manual-test'))
        );
    }

    finishAdHocTest(testRunId: TestRunId) {
        return this.testService.finishTestRun(testRunId).pipe(tap(() => {
            this.workbenchContextService.setContextActiveTestRun(undefined);
        }));
    }

    /**
     * Create a manual finding in the current workbench context.
     *
     * Observation targets are chosen in this order:
     * - explicit targets passed by the caller
     * - audit-derived target(s) for the currently active audit activity
     * - no observation targets at all
     *
     * The audit-derived fallback comes from AuditSession and is already normalized to the new
     * TestObject-centric model, so it may be a representative-state target or a coarse context target.
     */
    createNewManualFinding(optTitle?: string, optSeverity?: IssueSeverity, optFindingType?: FindingType, explicitObservationTargets?: ObservationTarget[], explicitAffectedSpecs?: SpecId[]): Observable<FindingId> {
        const currentContext = this.workbenchContextService.getCurrentWorkbenchContext();
        const activeTestRun = currentContext?.testRunContext?.activeTestRun;
        const auditContext = currentContext?.auditContext;
        const activeAudit = auditContext?.activeAudit;
        const findingTestRun = !!activeAudit ? undefined : activeTestRun;
        const projectId = currentContext?.project;
        let affectedSpecs: SpecId[] | undefined = explicitAffectedSpecs?.length ? explicitAffectedSpecs : undefined;
        let observationTargets: ObservationTarget[] | undefined = explicitObservationTargets?.length ? explicitObservationTargets : undefined;

        if (!activeTestRun && !activeAudit) {
            console.warn('No active test run or audit in workbench context. This should not happen when creating a new manual finding.');
        }

        if (!projectId) {
            return throwError(() => new Error('No active project in workbench context.'));
        }

        if (!!activeAudit) {
            const auditSession = this.workbenchAuditService.getOrCreateAuditSession(activeAudit);
            const activeActivity = auditContext?.activeAuditActivity;
            if (!affectedSpecs) {
                affectedSpecs = activeActivity?.assessedSpec ? [activeActivity.assessedSpec] : undefined;
            }
            if (!observationTargets) {
                observationTargets = auditSession.getObservationTargetsForActivity(activeActivity);
                if (observationTargets.length === 0) {
                    observationTargets = undefined;
                }
            }
        }

        const findingCreationData: FindingCreationData = {
            testRun: findingTestRun,
            audit: activeAudit,
            affectedSpecs,
            observationTargets,
            findingType: optFindingType ?? 'Manual.AdHocResult',
            severity: optSeverity,
            title: optTitle,
            issueData: {}, // TODO make this optional in backend
        };
        return this.testService.createFinding(projectId, findingCreationData);
    }

    /**
     * Create a batch of manual findings in the current workbench context in a single request.
     *
     * Each entry is expected to already carry its own content (title/severity/cells); this method
     * only attaches the active TestRun/Audit target, using the same mutually-exclusive resolution
     * (audit takes priority) as {@link createNewManualFinding}.
     */
    createFindingsBulk(entries: Omit<FindingCreationData, 'testRun' | 'audit'>[]): Observable<FindingId[]> {
        if (!entries.length) {
            return of([]);
        }

        const currentContext = this.workbenchContextService.getCurrentWorkbenchContext();
        const activeTestRun = currentContext?.testRunContext?.activeTestRun;
        const auditContext = currentContext?.auditContext;
        const activeAudit = auditContext?.activeAudit;
        const findingTestRun = !!activeAudit ? undefined : activeTestRun;
        const projectId = currentContext?.project;

        if (!activeTestRun && !activeAudit) {
            console.warn('No active test run or audit in workbench context. This should not happen when creating bulk findings.');
        }

        if (!projectId) {
            return throwError(() => new Error('No active project in workbench context.'));
        }

        let defaultAffectedSpecs: SpecId[] | undefined;
        let defaultObservationTargets: ObservationTarget[] | undefined;
        if (!!activeAudit) {
            const auditSession = this.workbenchAuditService.getOrCreateAuditSession(activeAudit);
            const activeActivity = auditContext?.activeAuditActivity;
            defaultAffectedSpecs = activeActivity?.assessedSpec ? [activeActivity.assessedSpec] : undefined;
            defaultObservationTargets = auditSession.getObservationTargetsForActivity(activeActivity);
            if (defaultObservationTargets.length === 0) {
                defaultObservationTargets = undefined;
            }
        }

        const findingCreationDatas: FindingCreationData[] = entries.map(entry => ({
            ...entry,
            testRun: findingTestRun,
            audit: activeAudit,
            affectedSpecs: entry.affectedSpecs?.length ? entry.affectedSpecs : defaultAffectedSpecs,
            observationTargets: entry.observationTargets?.length ? entry.observationTargets : defaultObservationTargets,
        }));

        return this.testService.createFindingsBulk(projectId, findingCreationDatas);
    }

    /**
     * Gets all findings (as FindingInfo) for the active audit - and there from the active activity - or test run in the workbench context. If both are active, findings from both are returned.
     * The idea is to show all relevant findings to the user in the workbench, no matter if they stem from the active test run or the active audit.
     */
    getRelevantFindingInfos(): Observable<FindingInfo[]> {
        const currentContext = this.workbenchContextService.getCurrentWorkbenchContext();
        if (!currentContext) {
            return of([]);
        }

        const activeTestRun = currentContext.testRunContext?.activeTestRun;
        const activeAudit = currentContext.auditContext?.activeAudit;
        const activeActivity = currentContext.auditContext?.activeAuditActivity;

        // Handle test run findings
        const testRunFindings$ = activeTestRun
            ? this.testService.getFindingsForTestRun(activeTestRun, true).fetch().pipe(
                    map(r => r.result)
            )
            : of([] as Finding[]);

        // Handle audit findings
        const auditFindings$ = activeAudit
            ? (() => {
                const testObjectId = activeActivity?.testObjectId;
                if (!testObjectId) {
                    return of([] as FindingInfo[]);
                }

                return this.testService.queryFindings$(currentContext.project, {
                    filterSpec: {
                        auditFilterSpec: [activeAudit],
                        affectedSpecs: activeActivity?.assessedSpec ? [activeActivity.assessedSpec] : undefined,
                        testObjectIds: [testObjectId]
                    }
                }).pipe(
                    map(res => (res.data.items as FindingSearchRecord[]).map(searchRecordToFindingInfo))
                );
            })()
            : of([] as FindingInfo[]);

        // Combine both sources
        return combineLatest({
            testRunFindings: testRunFindings$,
            auditFindings: auditFindings$
        }).pipe(
            map(({testRunFindings, auditFindings}) => {
                // merge and deduplicate findings
                const allFindingsMap = new Map<FindingId, FindingInfo>();
                testRunFindings.forEach(f => allFindingsMap.set(f.id, f));
                auditFindings.forEach(f => allFindingsMap.set(f.id, f));
                return Array.from(allFindingsMap.values());
            })
        );
    }

    getFindingUpdateEventsForProject(projectId: ProjectId) {
        return this.testService.getFindingUpdateEventsForProject(projectId);
    }

    addScreenshotToFinding(findingId: FindingId, screenshotId: ScreenshotId, optDescription?: string) {
        const contentCell = {
            type: 'ObservationContentCell',
            observation: {
                type: 'ScreenshotObservation',
                screenshotId: screenshotId,
            }
        }  as ContentCell;
        return this.addCellWithOptionalDescription(findingId, contentCell, optDescription);
    }

    addVideoToFinding(findingId: FindingId, videoId: VideoRecordingId, optDescription?: string) {
        const contentCell = {
            type: 'ObservationContentCell',
            observation: {
                type: 'VideoSnippetObservation',
                videoId: videoId
            }
        }  as ContentCell;
        return this.addCellWithOptionalDescription(findingId, contentCell, optDescription);
    }

    addTestMailToFinding(findingId: FindingId, testMailArtifactId: ArtifactId, optDescription?: string) {
        const contentCell = {
            type: 'ObservationContentCell',
            observation: {
                type: 'TestMailObservation',
                artifactId: testMailArtifactId
            }
        }  as ContentCell;
        return this.addCellWithOptionalDescription(findingId, contentCell, optDescription);
    }

    addTextToFinding(findingId: FindingId, text: string) {
        const contentCell = {
            type: 'TextContentCell',
            text: text
        } as ContentCell;
        return this.testService.appendCellToFinding(findingId, contentCell);
    }

    private addCellWithOptionalDescription(findingId: FindingId, cell: ContentCell, optDescription?: string) {
        const contentCells = [cell] as Array<ContentCell>;
        if (optDescription) {
            contentCells.push({
                type: 'TextContentCell',
                text: optDescription
            });
        }
        const pendingInsert = this.findingCellInsertService.consumePendingInsert(findingId);
        return this.testService.appendCellsToFinding(findingId, contentCells).pipe(
            switchMap(() => this.movePendingInsertedCells(findingId, pendingInsert?.insertAtIndex, pendingInsert?.originalSequence))
        );
    }

    private movePendingInsertedCells(findingId: FindingId, insertAtIndex?: number, originalSequence?: ContentCellId[]): Observable<void> {
        if (insertAtIndex === undefined || !originalSequence || insertAtIndex >= originalSequence.length) {
            return of(undefined);
        }

        const originalSet = new Set(originalSequence);
        return this.testService.getFinding$(findingId).pipe(
            map(updatedFinding => ({
                updatedFinding,
                newCellIds: updatedFinding.cellSequence.filter(id => !originalSet.has(id))
            })),
            filter(({ newCellIds }) => newCellIds.length > 0),
            take(1),
            switchMap(({ updatedFinding, newCellIds }) => {
                let predecessorId: ContentCellId | undefined = insertAtIndex === 0
                    ? undefined
                    : originalSequence[insertAtIndex - 1];
                return from(newCellIds).pipe(
                    concatMap(cellId => {
                        const pred = predecessorId;
                        predecessorId = cellId;
                        return this.testService.moveFindingCell(updatedFinding.id, cellId, pred);
                    }),
                    toArray(),
                    map(() => undefined)
                );
            })
        );
    }

    // TODO: Check if functions below are actually used/useful

    /**
     * Important operation used by various views to add a cell to the active finding. If there is not active finding, a new one is created.
     */
    appendCellToActiveFinding(cell: ContentCell): Observable<FindingId> {
        const context = this.workbenchContextService.getCurrentWorkbenchContext();
        const currentActiveFinding = context!.auditContext?.activeFinding;
        const currentProject = context!.project;

        if (!currentActiveFinding) {
            return this.createEmptyFindingInActiveContext().pipe(
                switchMap(newFindingId => this.testService.appendCellToFinding(newFindingId, cell).pipe(take(1), map(() => newFindingId))),
                switchMap(newFindingId => this.testService.getFinding$(newFindingId).pipe(take(1))),
                tap(finding => this.workbenchContextService.setContextActiveFinding(finding.id)),
                map(finding => finding.id)
            );
        } else {
            if (this.currentPseudoActiveFinding && currentActiveFinding === this.currentPseudoActiveFinding?.id) {
                // we need to create the pseudo active finding in the backend, first
                return this.createNewManualFinding().pipe(switchMap(_ => {
                    this.currentPseudoActiveFinding = undefined;
                    return this.testService.appendCellToFinding(currentActiveFinding, cell).pipe(map(() => currentActiveFinding));
                }));
            } else {
                return this.testService.appendCellToFinding(currentActiveFinding, cell).pipe(map(() => currentActiveFinding));
            }
        }
    }

    createEmptyFindingInActiveContext(): Observable<FindingId> {
        const currentContext = this.workbenchContextService.getCurrentWorkbenchContext();
        const activeAudit = currentContext!.auditContext?.activeAudit;
        const activeTestRun = currentContext!.expeditionContext?.activeTestRun;

        return this.testService.createEmptyFinding(currentContext!.project, activeTestRun, activeAudit).pipe(
            take(1),
            switchMap(findingId => this.testService.getFinding$(findingId).pipe(take(1))),
            map(finding => finding.id)
        );
    }

    activateFindingForAuditActivity(auditId: AuditId, projectId: ProjectId, specId: SpecId, observationTarget: ObservationTarget): Observable<FindingId> {
        const testObjectId = observationTarget.testObjectId;
        if (!testObjectId) {
            return throwError(() => new Error('No testObjectId provided for finding activation.'));
        }

        return this.testService.queryFindings$(projectId, {
            filterSpec: {
                auditFilterSpec: [auditId],
                affectedSpecs: [specId],
                testObjectIds: [testObjectId],
            }
        }).pipe(
            map(paginatedResult => ({findings: paginatedResult.data.items as FindingSearchRecord[]})),
            switchMap(({findings}) => {
                const candidateResults = findings;

                if (candidateResults.length === 0) {

                    if (!observationTarget || !specId) {
                        console.error('No observation target or spec id provided for creating empty finding');
                        return throwError(() => new Error('No observation target or spec id provided for creating empty finding'));
                    } else {
                        // activate pseudo finding
                        const newFindingId = getRandomUUID();
                        this.workbenchContextService.setContextPseudoFinding({
                            id: newFindingId,
                            findingType: 'Generic.Finding',
                            audit: auditId,
                            affectedSpecs: [specId],
                            observationTargets: [observationTarget],
                            severity: 'error',
                            associatedArtifacts: [],
                            issueData: {
                                cellSequence: [],
                                cells: {}
                            },
                        });
                        this.workbenchContextService.setContextActiveFinding(newFindingId);
                        return of(newFindingId);

                    }
                } else {
                    // activate first finding
                    return of(candidateResults[0].id);
                }
            }),
            tap(newFindingId => {
                this.workbenchContextService.setContextActiveFinding(newFindingId);
            })
        );
    }
}
